import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex, Tag } from "antd";
import { ColumnsType } from "antd/es/table";

import SText from "@/styles/SText";
import { CountrySelect } from "@/components/Selector";
import { ShipAddress, ShipAddressTypeOption } from "@/types/shipAddress";

interface UseShipAddressColumnProps {
  typeOptions: Array<ShipAddressTypeOption>;
  settingDefaultId: string | null;
  onDetail: (row: ShipAddress) => void;
  onDel: (id: string) => void;
  onSetDefault: (id: string) => void;
}

const useShipAddressColumn = ({
  typeOptions,
  settingDefaultId,
  onDetail,
  onDel,
  onSetDefault,
}: UseShipAddressColumnProps): ColumnsType<ShipAddress> => {
  const { t } = useTranslation();

  return useMemo(() => [
    {
      title: <SText text={t("sale.shipAddressName")} />,
      dataIndex: "name",
      width: 160,
      render: value => <SText text={value ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.address")} />,
      dataIndex: "address",
      width: 260,
      render: value => <SText text={value ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.phone")} />,
      dataIndex: "phone",
      width: 130,
      render: value => <SText text={value ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.country")} />,
      dataIndex: "country",
      width: 120,
      render: (value, row) => <CountrySelect key={row.id} value={value || ""} readOnly />,
    },
    {
      title: <SText text={t("sale.shipAddressType")} />,
      dataIndex: "type",
      width: 130,
      render: (_, row) => (
        <SText
          text={row.typeDesc || typeOptions.find(item => item.key === row.type)?.desc || ""}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: <SText text={t("sale.defaultAddress")} />,
      dataIndex: "default",
      width: 100,
      align: "center" as const,
      render: value => value === 1
        ? <Tag color="success">{t("sale.defaultAddress")}</Tag>
        : t("common.no"),
    },
    {
      title: <SText text={t("sale.remark")} />,
      dataIndex: "remark",
      width: 180,
      render: value => <SText text={value ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("common.opt"),
      dataIndex: "operation",
      fixed: "right",
      width: 200,
      render: (_, row) => (
        <Flex>
          <Button
            type="link"
            size="small"
            disabled={row.default === 1 || settingDefaultId !== null}
            loading={settingDefaultId === row.id}
            onClick={() => onSetDefault(row.id)}
          >
            {t("sale.setDefault")}
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => onDetail(row)}
          >
            {t("common.edit")}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            onClick={() => onDel(row.id)}
          >
            {t("common.del")}
          </Button>
        </Flex>
      ),
    },
  ], [onDel, onDetail, onSetDefault, settingDefaultId, t, typeOptions]);
};

export default useShipAddressColumn;
